import React from 'react';
import { Book, Heart, Mail, Facebook } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { bookInfo, socialLinks } from '../data/mockData';

const BookPage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-amber-50 to-orange-50 py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <div className="inline-block px-4 py-2 bg-amber-600 text-white text-sm font-semibold rounded-full mb-6">
                {bookInfo.status}
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
                {bookInfo.title}
              </h1>
              <p className="text-2xl text-amber-700 font-semibold mb-6">
                {bookInfo.subtitle}
              </p>
              <p className="text-xl text-gray-700 leading-relaxed mb-8">
                {bookInfo.description}
              </p>
              <div className="space-y-4">
                <a href={`mailto:${socialLinks.email}?subject=Book Interest - Two Sandwiches and a Nap`}>
                  <Button size="lg" className="w-full sm:w-auto bg-amber-600 hover:bg-amber-700 text-white px-8 py-6 text-lg">
                    <Mail className="mr-2 w-5 h-5" />
                    Get Updates About the Book
                  </Button>
                </a>
                <a
                  href={socialLinks.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                >
                  <Button size="lg" variant="outline" className="w-full sm:w-auto border-2 border-amber-600 text-amber-600 hover:bg-amber-50 px-8 py-6 text-lg">
                    <Facebook className="mr-2 w-5 h-5" />
                    Follow on Facebook
                  </Button>
                </a>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <img
                src={bookInfo.coverImage}
                alt="Two Sandwiches and a Nap Book Cover"
                className="rounded-2xl shadow-2xl w-full max-w-lg mx-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* About the Book */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Book className="w-16 h-16 text-amber-600 mx-auto mb-6" />
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              About the Book
            </h2>
          </div>

          <div className="prose prose-lg max-w-none">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              "Two Sandwiches and a Nap" is more than just a book about addiction recovery - 
              it's a honest, raw, and hopeful account of one person's journey through the darkness 
              and into the light.
            </p>

            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Through personal stories, hard-won insights, and moments of both struggle and triumph, 
              this book explores what it really means to recover. Not the Instagram-perfect version 
              of recovery, but the messy, difficult, beautiful reality of it.
            </p>

            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              The title comes from a simple piece of advice that changed everything: when you're 
              overwhelmed, when you don't know what to do, when everything feels impossible - 
              eat two sandwiches, take a nap, and wake up to try again. It's a reminder that 
              recovery starts with the basics: taking care of yourself with compassion.
            </p>

            <div className="bg-teal-50 border-l-4 border-teal-600 p-6 my-8">
              <p className="text-xl text-teal-900 italic">
                "This book is for anyone who has ever felt lost, anyone who has struggled, 
                and anyone who needs to know that recovery is possible. You are not alone."
              </p>
            </div>

            <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-12">
              What You'll Find in These Pages
            </h3>
            <ul className="space-y-3 text-lg text-gray-700 mb-8">
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 mt-1">•</span>
                <span>Real stories from the journey of addiction and recovery</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 mt-1">•</span>
                <span>Honest reflections on struggle, relapse, and redemption</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 mt-1">•</span>
                <span>Practical wisdom on self-care and self-compassion</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 mt-1">•</span>
                <span>Hope for those still struggling</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 mt-1">•</span>
                <span>Guidance for loved ones supporting someone in recovery</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Stay Updated */}
      <section className="py-20 bg-gradient-to-br from-teal-50 to-cyan-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-2 border-teal-200">
            <CardContent className="p-10 text-center">
              <Heart className="w-16 h-16 text-red-500 mx-auto mb-6" />
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Stay Updated on the Book Release
              </h2>
              <p className="text-xl text-gray-700 mb-8">
                Want to know when "Two Sandwiches and a Nap" is published? 
                Follow us on Facebook or send us an email to get updates.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href={`mailto:${socialLinks.email}?subject=Book Updates`}>
                  <Button size="lg" className="w-full sm:w-auto bg-teal-600 hover:bg-teal-700 text-white px-8 py-4">
                    <Mail className="mr-2 w-5 h-5" />
                    Email for Updates
                  </Button>
                </a>
                <a
                  href={socialLinks.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button size="lg" variant="outline" className="w-full sm:w-auto border-2 border-teal-600 text-teal-600 hover:bg-teal-50 px-8 py-4">
                    <Facebook className="mr-2 w-5 h-5" />
                    Follow on Facebook
                  </Button>
                </a>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Author's Note */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            A Note from the Author
          </h2>
          <p className="text-lg text-gray-700 leading-relaxed italic mb-6">
            "I wrote this book because I needed to read it when I was struggling. I wrote it for 
            anyone who feels alone in their battle with addiction. I wrote it to say: recovery is 
            messy, it's hard, but it's absolutely possible. And you don't have to be perfect - 
            sometimes you just need two sandwiches and a nap."
          </p>
          <p className="text-xl text-gray-900 font-semibold">
            - Two Sandwiches and a Nap
          </p>
        </div>
      </section>
    </div>
  );
};

export default BookPage;
